import { Alert, Cascader, Form, Modal, message } from 'antd'
import { useEffect, useState } from 'react'

import {
  adjustStudentCourseProgress,
  getStudentCurrentCourseProgress,
  type CurrentCourseProgress,
} from '~/services/course'
import { getTextBookTree } from '~/services/textbook-category'
import {
  getTextbookByCategoryId,
  getTextbookByParentId,
} from '~/services/textbook'
import { IStudent } from '~/types/student'
import { ITextbook } from '~/types/textbook'

/** 教材树到章节共三级：教材(1) → 单元(2) → 章节(3)。后端只接受三级教材。 */
interface BookOption {
  value: number
  label: string
  level: number
  isLeaf: boolean
  children?: BookOption[]
  loading?: boolean
}

const toBookOption = (item: ITextbook): BookOption => ({
  value: item.id,
  label: item.title,
  level: item.level,
  isLeaf: item.level >= 3,
})

interface Values {
  category?: number[]
  textbook?: number[]
}

export default function TextbookProgressModal({
  student,
  onClose,
}: {
  student: IStudent
  onClose: () => void
}) {
  const [form] = Form.useForm<Values>()
  const [categoryTree, setCategoryTree] = useState<unknown[]>([])
  const [bookOptions, setBookOptions] = useState<BookOption[]>([])
  const [saving, setSaving] = useState(false)
  const [current, setCurrent] = useState<CurrentCourseProgress | null>()
  const category = Form.useWatch('category', form)
  const categoryId = category?.[category.length - 1]

  useEffect(() => {
    getTextBookTree()
      .then((res) => setCategoryTree(res.data || []))
      .catch(() => message.error('教材分类加载失败'))
  }, [])

  // Show where the student currently is before the admin changes it. undefined = still loading,
  // null = no progress yet.
  useEffect(() => {
    getStudentCurrentCourseProgress(Number(student.id))
      .then((res) => setCurrent(res.data ?? null))
      .catch(() => setCurrent(null))
  }, [student.id])

  // Chosen category → its level-1 textbooks become the root of the chapter picker.
  useEffect(() => {
    form.setFieldValue('textbook', undefined)
    setBookOptions([])
    if (!categoryId) return
    getTextbookByCategoryId(categoryId)
      .then((res) => setBookOptions((res.data || []).map(toBookOption)))
      .catch(() => message.error('教材加载失败'))
  }, [categoryId, form])

  // Lazy-load one level deeper (unit → chapter) as the admin drills in.
  const loadBookChildren = async (selected: BookOption[]) => {
    const target = selected[selected.length - 1]
    target.loading = true
    setBookOptions((prev) => [...prev])
    try {
      const res = await getTextbookByParentId(target.value)
      target.children = (res.data || []).map(toBookOption)
    } catch {
      message.error('下级教材加载失败')
    } finally {
      target.loading = false
      setBookOptions((prev) => [...prev])
    }
  }

  const submit = async () => {
    const values = await form.validateFields()
    const textbookId = values.textbook?.[values.textbook.length - 1]
    if (!textbookId) return
    setSaving(true)
    try {
      await adjustStudentCourseProgress({
        studentId: Number(student.id),
        textbookId,
      })
      message.success('教材进度已更新')
      onClose()
    } catch (err) {
      message.error((err as Error)?.message || '更新失败，请稍后再试～')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      title={`教材管理 · ${student.name}`}
      open
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText="更新进度"
      cancelText="取消"
      destroyOnClose
    >
      <Alert
        type="info"
        message={
          current === undefined
            ? '正在读取当前教材…'
            : current?.preChapter || current?.textbook
              ? `当前教材：${[current.textbook?.title, current.preChapter?.title]
                  .filter(Boolean)
                  .join(' · ')}（进度 ${Math.round((current.progress || 0) * 100)}%）`
            : '该学生还没有教材进度记录。'
        }
        description="选择学生当前所学的章节，进度只能前进、不能回退。"
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical">
        <Form.Item
          label="教材分类"
          name="category"
          rules={[{ required: true, message: '请选择教材分类' }]}
        >
          <Cascader
            placeholder="请选择教材分类"
            options={categoryTree as never}
            fieldNames={{ value: 'id', label: 'name' }}
            changeOnSelect
          />
        </Form.Item>
        <Form.Item
          label="章节（三级教材）"
          name="textbook"
          rules={[
            { required: true, message: '请选择到具体章节' },
            {
              validator: (_, value: number[] | undefined) =>
                value && value.length === 3
                  ? Promise.resolve()
                  : Promise.reject(new Error('必须选择到第三级章节')),
            },
          ]}
        >
          <Cascader
            placeholder={categoryId ? '请选择章节' : '请先选择教材分类'}
            disabled={!categoryId}
            options={bookOptions}
            loadData={loadBookChildren as never}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
